import React, {useCallback} from 'react'
import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar} from '@mui/material'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import {ThemeColorsHospitalic} from '@/assets/styles/color/ThemeColorsHospitalic'
import { useLazyQuery, useMutation} from '@apollo/client'
import Alert from '@mui/material/Alert'
import {
    CANCEL_PENDING_JOB,
    VARIANT_DATABASE_JOBS_QUERY,
    VARIANT_DATASET_JOBS_QUERY
} from '@/graphql/query/queries'

type CancelDatasetJobDialogProps = {
    job: any;
    open: boolean;
    onClose: () => void;
    type: 'dataset' | 'database';
}

const CancelDatasetJobDialog = ({job, open, onClose, type}: CancelDatasetJobDialogProps) => {
    const [_getDatasetJobList, {
        refetch: refetchDatasetJobs
    }] = useLazyQuery(VARIANT_DATASET_JOBS_QUERY, {notifyOnNetworkStatusChange: true, fetchPolicy: 'network-only'})

    const [_getDatabaseJobList, {
        refetch: refetchDatabaseJobs
    }] = useLazyQuery(VARIANT_DATABASE_JOBS_QUERY, {notifyOnNetworkStatusChange: true, fetchPolicy: 'network-only'})

    const [cancelJob, {loading: cancelJobLoading, error: cancelJobError, reset}] = useMutation(CANCEL_PENDING_JOB)

    const handleConfirm = useCallback(async () => {
        try {
            await cancelJob({variables: {jobId: job.id}})
            if (type === 'dataset') {
                await refetchDatasetJobs()
            } else {
                await refetchDatabaseJobs()
            }
            onClose()
        } catch (err) {
            // do nothing - error will be displayed via cancelJobError
            console.warn('Unable to cancel job', err)
        }
    }, [])

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                aria-labelledby="cancel-dialog-title"
                aria-describedby="cancel-dialog-description"
            >
                <DialogTitle id="cancel-dialog-title" variant="h5">
                    Are you sure you want to cancel {type} job?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="cancel-dialog-description">
                        <Typography color={ThemeColorsHospitalic.TEXT_PRIMARY}>Job id: {job.id}</Typography>
                        <Typography color={ThemeColorsHospitalic.TEXT_PRIMARY}>Job name: {type === 'dataset' ? job.dataset_name : job.database_name}</Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} variant="outlined" size="small">Back</Button>
                    <Button onClick={handleConfirm} loading={cancelJobLoading} autoFocus variant="contained" size="small">
                        Cancel {type} job
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={!!cancelJobError}
                autoHideDuration={3000} onClose={reset}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}>
                <Alert
                    onClose={reset}
                    severity="error"
                    variant="filled"
                    sx={{width: '100%'}}
                >
                    Error canceling job with id {job.id}
                </Alert>
            </Snackbar>
        </>
    )
}

export default CancelDatasetJobDialog